import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { requireData } from "../../shared/api/api-error.js";
import { useApi } from "../../shared/api/api-context.js";
import type { components } from "../../shared/api/generated/trading-engine.js";
import { accountView, connectionView } from "./models.js";

type Settings = components["schemas"]["Settings"];
type TastytradeCredentials = components["schemas"]["TastytradeCredentials"];

export const accountKeys = { all: ["broker-accounts"] as const };
export const connectionKeys = { all: ["broker-connections"] as const };
export const settingsKeys = { current: ["settings"] as const };

export function useConnections() {
  const api = useApi();
  return useQuery({
    queryKey: connectionKeys.all,
    queryFn: async ({ signal }) => {
      const page = requireData(
        await api.GET("/connections", { params: { query: { limit: 100 } }, signal }),
      );
      return page.items.map(connectionView);
    },
  });
}

export function useAccounts(connectionId?: string) {
  const api = useApi();
  return useQuery({
    queryKey: [...accountKeys.all, connectionId ?? "all"],
    queryFn: async ({ signal }) => {
      const page = requireData(
        await api.GET("/accounts", {
          params: {
            query: { limit: 100, ...(connectionId === undefined ? {} : { connectionId }) },
          },
          signal,
        }),
      );
      return page.items.map(accountView);
    },
  });
}

export function useConnectTastytrade() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (credentials: TastytradeCredentials) =>
      requireData(
        await api.POST("/connections/tastytrade", {
          body: credentials,
          params: { header: { "x-idempotency-key": crypto.randomUUID() } },
        }),
      ),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: connectionKeys.all }),
        queryClient.invalidateQueries({ queryKey: accountKeys.all }),
      ]);
    },
  });
}

export function useConnectionAction() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      connectionId,
      action,
    }: {
      connectionId: string;
      action: "disable" | "refresh";
    }) =>
      requireData(
        await api.POST("/connections/{connectionId}/{action}", {
          params: {
            header: { "x-idempotency-key": crypto.randomUUID() },
            path: { action, connectionId },
          },
        }),
      ),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: connectionKeys.all }),
        queryClient.invalidateQueries({ queryKey: accountKeys.all }),
      ]);
    },
  });
}

export function useSettings() {
  const api = useApi();
  return useQuery({
    queryKey: settingsKeys.current,
    queryFn: async ({ signal }) => requireData(await api.GET("/settings", { signal })),
  });
}

export function useSaveSettings() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (settings: Settings) =>
      requireData(await api.PUT("/settings", { body: settings })),
    onSuccess: (settings) => {
      queryClient.setQueryData(settingsKeys.current, settings);
    },
  });
}
